import React from "react";
import { KopHeader } from "./PrintHeader";
import { kriteriaWarna } from "@/lib/utils";

/**
 * Dokumen PERHITUNGAN NILAI SIKAP DAN PERILAKU PERORANGAN (landscape A4).
 * 4 kolom tetap (NO, URAIAN, KODE, NILAI) + 23 kolom unsur dalam 3 kategori.
 *
 * props:
 *   settings, siswa, periode, catatan_siku (59 items dengan kode & skor),
 *   sosio_rata_per_unsur (array 23), unsur_penilaian (23), kode_to_unsur (map "1"-"23" -> [kode])
 */
export default function PrintNilaiGabungan({
  settings = {},
  siswa = {},
  periode = {},
  catatan_siku = [],
  sosio_rata_per_unsur = [],
  unsur_penilaian = [],
  kode_to_unsur = {},
  rangking = null,
  nilai_akhir = null,
  showSignature = true,
}) {
  // Build lookup: kode -> skor
  const kodeSkor = {};
  catatan_siku.forEach((c) => { kodeSkor[c.kode] = c.skor; });

  // For each unsur (1-23), which kodes belong (set)
  const unsurKodes = {};
  for (let u = 1; u <= 23; u++) {
    const arr = kode_to_unsur[String(u)] || [];
    unsurKodes[u] = new Set(arr);
  }

  // Kategori grouping based on unsur_penilaian
  const groups = [];
  let cur = null;
  unsur_penilaian.forEach((u) => {
    if (!cur || cur.kategori !== u.kategori) {
      cur = { kategori: u.kategori, label: u.kategori_label, items: [] };
      groups.push(cur);
    }
    cur.items.push(u);
  });

  // Compute Skor Observasi per kolom = rata-rata skor (4/5) dari indikator yang mapped
  const skorObsKolom = [];
  for (let u = 1; u <= 23; u++) {
    const kset = unsurKodes[u];
    const vals = catatan_siku.filter((c) => kset.has(c.kode)).map((c) => c.skor);
    if (vals.length === 0) skorObsKolom.push(null);
    else skorObsKolom.push(vals.reduce((a, b) => a + b, 0) / vals.length);
  }
  // Skor Sosiometri per kolom (rata-rata A,B,C per unsur)
  const skorSosio = sosio_rata_per_unsur.slice(0, 23);
  // Rata-rata Nilai per kolom = (obs + sosio) / 2
  const rataNilai = [];
  for (let i = 0; i < 23; i++) {
    const o = skorObsKolom[i];
    const s = skorSosio[i];
    const arr = [o, s].filter((v) => v != null && v > 0);
    rataNilai.push(arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0);
  }
  // Kriteria per kolom
  const kriteria = rataNilai.map((v) => v >= 4.5 ? "BS" : v >= 3.5 ? "B" : v >= 2.5 ? "C" : "K");

  // Nilai Unsur per kategori — formula DAPAT DIATUR di Settings (default sesuai Excel):
  // Mental Rohani   = SUM(kolom 1-4)  × koef_rohani
  // Mental Ideologi = SUM(kolom 5-6)  × koef_ideologi
  // Mental Kejuangan= SUM(kolom 7-23) × koef_kejuangan
  // Nilai Sikku     = (MR + MI + MK) / divisor_sikku
  const koefR = Number(settings.koef_rohani ?? 47);
  const koefI = Number(settings.koef_ideologi ?? 91);
  const koefK = Number(settings.koef_kejuangan ?? 100 / 9);
  const divS = Number(settings.divisor_sikku ?? 3) || 3;
  const nilaiUnsur = { A: 0, B: 0, C: 0 };
  const sumByCat = { A: 0, B: 0, C: 0 };
  unsur_penilaian.forEach((u, i) => {
    sumByCat[u.kategori] = (sumByCat[u.kategori] || 0) + (rataNilai[i] || 0);
  });
  nilaiUnsur.A = sumByCat.A * koefR;
  nilaiUnsur.B = sumByCat.B * koefI;
  nilaiUnsur.C = sumByCat.C * koefK;
  const nilaiSikkuRumus = (nilaiUnsur.A + nilaiUnsur.B + nilaiUnsur.C) / divS;
  // Nilai Siku ditampilkan sama dengan Nilai Akhir (jika diberikan dari backend),
  // jika tidak ada, fallback ke hasil rumus.
  const nilaiSikku = nilai_akhir != null ? Number(nilai_akhir) : nilaiSikkuRumus;

  // Shaded cell style
  const shadeStyle = { background: "repeating-linear-gradient(45deg, #d4d4d4, #d4d4d4 3px, #e8e8e8 3px, #e8e8e8 6px)" };

  // Column widths (compact for landscape)
  const colWidth = 38;
  const cellPad = "2px 3px";
  const baseFontSize = "8pt";

  const headerThStyle = {
    border: "1px solid #000",
    padding: "3px 2px",
    fontSize: "7.5pt",
    background: "#fff",
    textAlign: "center",
    fontWeight: "normal",
    verticalAlign: "middle",
    lineHeight: 1.1,
  };
  const cellTdStyle = {
    border: "1px solid #000",
    padding: cellPad,
    fontSize: baseFontSize,
    textAlign: "center",
    verticalAlign: "middle",
  };
  const cellShade = { ...cellTdStyle, ...shadeStyle };

  return (
    <div
      className="print-doc bg-white text-black mx-auto"
      style={{
        width: "287mm", // A4 landscape minus margin
        padding: "8mm 10mm",
        fontFamily: "Arial, sans-serif",
        fontSize: baseFontSize,
        boxShadow: "0 0 8px rgba(0,0,0,0.08)",
      }}
    >
      {/* Header row: KOP kiri + Judul kanan */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
        <KopHeader line1={settings.kop_l1} line2={settings.kop_l2} />
        <div style={{ flex: 1, textAlign: "center", paddingTop: 6 }}>
          <div style={{ fontWeight: "bold", fontSize: "11pt" }}>PERHITUNGAN NILAI SIKAP DAN PERILAKU PERORANGAN</div>
        </div>
        <div style={{ width: 180 }} />
      </div>

      {/* Identitas */}
      <table style={{ marginBottom: 6, border: "none", width: "100%", fontSize: "9pt" }}>
        <tbody>
          <tr>
            <td style={{ border: "none", width: 120, padding: "1px 4px" }}>Pendidikan</td>
            <td style={{ border: "none", width: 8, padding: "1px 0" }}>:</td>
            <td style={{ border: "none", padding: "1px 4px" }}>{settings.pendidikan}</td>
            <td style={{ border: "none", padding: "1px 4px", width: 90, textAlign: "right" }}>{periode.jenis === "bulanan" ? "Bulanan ke" : "Mingguan ke"}</td>
            <td style={{ border: "none", padding: "1px 0", width: 8 }}>:</td>
            <td style={{ border: "none", padding: "1px 4px", width: 80, fontWeight: "bold" }}>{periode.nomor ?? "—"}</td>
          </tr>
          <tr>
            <td style={{ border: "none", padding: "1px 4px" }}>Periode Penilaian</td>
            <td style={{ border: "none", padding: "1px 0" }}>:</td>
            <td style={{ border: "none", padding: "1px 4px" }} colSpan="4">{periode.bulan} {periode.tahun}</td>
          </tr>
          <tr>
            <td style={{ border: "none", padding: "1px 4px" }}>Nama</td>
            <td style={{ border: "none", padding: "1px 0" }}>:</td>
            <td style={{ border: "none", padding: "1px 4px", fontWeight: "bold" }} colSpan="4">{siswa.nama}</td>
          </tr>
          <tr>
            <td style={{ border: "none", padding: "1px 4px" }}>Nosis/No AK/NIM</td>
            <td style={{ border: "none", padding: "1px 0" }}>:</td>
            <td style={{ border: "none", padding: "1px 4px" }} colSpan="4">{siswa.nosis}</td>
          </tr>
        </tbody>
      </table>

      {/* Tabel utama */}
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: baseFontSize }}>
        <thead>
          {/* Row 1: Master header */}
          <tr>
            <th rowSpan="3" style={{ ...headerThStyle, width: 22 }}>NO</th>
            <th rowSpan="3" style={{ ...headerThStyle, width: 220, textAlign: "left", paddingLeft: 5 }}>URAIAN KEGIATAN</th>
            <th rowSpan="3" style={{ ...headerThStyle, width: 36 }}>KODE</th>
            <th rowSpan="3" style={{ ...headerThStyle, width: 34 }}>NILAI</th>
            <th colSpan="23" style={{ ...headerThStyle, fontWeight: "bold" }}>UNSUR PENILAIAN</th>
          </tr>
          {/* Row 2: Kategori */}
          <tr>
            <th colSpan="4" style={{ ...headerThStyle, fontWeight: "bold" }}>MENTAL ROHANI</th>
            <th colSpan="2" style={{ ...headerThStyle, fontWeight: "bold" }}>MENTAL IDEOLOGI</th>
            <th colSpan="17" style={{ ...headerThStyle, fontWeight: "bold" }}>MENTAL KEJUANGAN</th>
          </tr>
          {/* Row 3: Nama unsur */}
          <tr>
            {unsur_penilaian.map((u) => (
              <th key={u.no} style={{ ...headerThStyle, width: colWidth, fontSize: "6.5pt", padding: "2px 1px" }}>
                <div style={{ writingMode: "vertical-rl", transform: "rotate(180deg)", minHeight: 86, display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center" }}>{u.nama}</div>
              </th>
            ))}
          </tr>
          {/* Row 4: column numbers */}
          <tr>
            <th style={headerThStyle}>1</th>
            <th style={headerThStyle}>2</th>
            <th style={headerThStyle}>3</th>
            <th style={headerThStyle}>4</th>
            {unsur_penilaian.map((u, i) => (
              <th key={u.no} style={headerThStyle}>{5 + i}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {catatan_siku.map((c) => (
            <tr key={c.no}>
              <td style={cellTdStyle}>{c.no}</td>
              <td style={{ ...cellTdStyle, textAlign: "left", paddingLeft: 5, fontSize: "7.5pt", lineHeight: 1.15 }}>{c.teks}</td>
              <td style={{ ...cellTdStyle, fontWeight: "bold" }}>{c.kode}</td>
              <td style={{ ...cellTdStyle, fontWeight: "bold" }}>{c.skor}</td>
              {unsur_penilaian.map((u) => {
                const inMap = unsurKodes[u.no].has(c.kode);
                return (
                  <td key={u.no} style={inMap ? cellTdStyle : cellShade}>{inMap ? c.skor : ""}</td>
                );
              })}
            </tr>
          ))}

          {/* Footer rows */}
          <tr style={{ background: "#f5f5f5" }}>
            <td colSpan="4" style={{ ...cellTdStyle, textAlign: "right", fontWeight: "bold", paddingRight: 6 }}>Skor Observasi</td>
            {skorObsKolom.map((v, i) => (
              <td key={i} style={{ ...cellTdStyle, fontWeight: "bold" }}>{v != null ? v.toFixed(2) : "—"}</td>
            ))}
          </tr>
          <tr style={{ background: "#f5f5f5" }}>
            <td colSpan="4" style={{ ...cellTdStyle, textAlign: "right", fontWeight: "bold", paddingRight: 6 }}>Skor Sosiometri</td>
            {skorSosio.map((v, i) => (
              <td key={i} style={{ ...cellTdStyle, fontWeight: "bold" }}>{v ? v.toFixed(2) : "—"}</td>
            ))}
          </tr>
          <tr style={{ background: "#fafafa" }}>
            <td colSpan="4" style={{ ...cellTdStyle, textAlign: "right", fontWeight: "bold", paddingRight: 6 }}>Rata-rata Nilai</td>
            {rataNilai.map((v, i) => (
              <td key={i} style={{ ...cellTdStyle, fontWeight: "bold" }}>{v ? v.toFixed(2) : "—"}</td>
            ))}
          </tr>
          <tr>
            <td colSpan="4" style={{ ...cellTdStyle, textAlign: "right", fontWeight: "bold", paddingRight: 6 }}>Kriteria</td>
            {kriteria.map((k, i) => (
              <td key={i} style={{ ...cellTdStyle, fontWeight: "bold" }}>{k}</td>
            ))}
          </tr>
          {/* Nilai Unsur */}
          <tr style={{ background: "#eef2f7" }}>
            <td colSpan="4" style={{ ...cellTdStyle, textAlign: "right", fontWeight: "bold", paddingRight: 6 }}>Nilai Unsur</td>
            <td colSpan="4" style={{ ...cellTdStyle, fontWeight: "bold", fontSize: "9pt" }}>{nilaiUnsur.A.toFixed(2)}</td>
            <td colSpan="2" style={{ ...cellTdStyle, fontWeight: "bold", fontSize: "9pt" }}>{nilaiUnsur.B.toFixed(2)}</td>
            <td colSpan="17" style={{ ...cellTdStyle, fontWeight: "bold", fontSize: "9pt" }}>{nilaiUnsur.C.toFixed(2)}</td>
          </tr>
          {/* Nilai Sikku (= Nilai Akhir) */}
          <tr style={{ background: "#dde7f3" }}>
            <td colSpan="4" style={{ ...cellTdStyle, textAlign: "right", fontWeight: "bold", paddingRight: 6, fontSize: "10pt" }}>Nilai Sikku{rangking ? ` (Rk ${rangking})` : ""}</td>
            <td colSpan="23" style={{ ...cellTdStyle, fontWeight: "bold", fontSize: "10pt" }}>{nilaiSikku.toFixed(2)}</td>
          </tr>
        </tbody>
      </table>

      {/* Kriteria legend */}
      <div style={{ marginTop: 6, fontSize: "7.5pt", color: "#444" }}>
        <strong>Kriteria:</strong> BS = Baik Sekali (≥4.50) • B = Baik (3.50–4.49) • C = Cukup (2.50–3.49) • K = Kurang (&lt;2.50)
      </div>

      {/* TTD */}
      {showSignature && (
        <div style={{ marginTop: 30, display: "flex", justifyContent: "flex-end", fontFamily: "Arial, sans-serif", fontSize: "10pt" }}>
          <div style={{ width: 280, textAlign: "center" }}>
            <div>{settings.lokasi}, {(periode && periode.bulan) || settings.bulan} {(periode && periode.tahun) || settings.tahun}</div>
            <div>{settings.jabatan_penandatangan},</div>
            <div style={{ height: 60 }} />
            <div style={{ fontWeight: "bold" }}>{settings.nama_komandan}</div>
            <div>{settings.pangkat_komandan} NRP {settings.nrp_komandan}</div>
          </div>
        </div>
      )}
    </div>
  );
}
